// Story level rewards — edit treats / packs / cosmetics / 2× eligibility here.
// Feature unlocks are derived from storyLevels.js (gameplay locks stay there).
// Regenerate docs/story-level-rewards.md after edits: npm run story-rewards-doc

import {
    STORY_LEVELS,
    getStoryLevelById,
} from "./storyLevels";
import { getCosmeticById } from "../utils/cosmetics";
import { STORE_UNLOCK_STORY_LEVEL } from "../utils/storyProgress";

/** Default treats per Story clear when a level omits an override. */
export const DEFAULT_STORY_LEVEL_TREATS = 500;

const FEATURE_UNLOCK_LABELS = {
    endlessBoard4: "Endless 2×2",
    endlessHighScore: "High Score tracking",
    endlessBoard9: "Endless 3×3",
    hardcoreMode: "Hardcore (Endless)",
    endlessBoard16: "Endless 4×4",
    fullCustomEndless: "Full custom Endless",
    blitzMode: "Blitz (Endless)",
    storyHardMode: "Story Hard Mode",
    twistedMode: "Twisted (Endless)",
    shyCorgisMode: "Shy Corgis (Endless)",
    anxiousMode: "Anxious (Endless)",
};

/**
 * Editable reward rows. Add keys only when overriding defaults.
 *
 * | Field              | Default                         |
 * |--------------------|---------------------------------|
 * | treats             | DEFAULT_STORY_LEVEL_TREATS      |
 * | packs              | 0                               |
 * | boostBundle        | 0 (1 = Reveal+Shield+Freeze) |
 * | doubleAdEligible   | true (only applies when treats) |
 * | cosmeticRewardIds  | []                              |
 *
 * Boost bundles are granted at milestone clears where players can use them:
 * L4 (hearts + bigger board), L9 (4×4 after Hardcore), L13 (Underworld), L19 (finale).
 */
export const STORY_LEVEL_REWARD_OVERRIDES = {
    1: { treats: 500, packs: 0, doubleAdEligible: true },
    2: { treats: 500, packs: 0, doubleAdEligible: true },
    3: { treats: 500, packs: 0, doubleAdEligible: true },
    4: { treats: 0, packs: 1, boostBundle: 1, doubleAdEligible: false },
    5: { treats: 500, packs: 0, doubleAdEligible: true },
    6: { treats: 500, packs: 0, doubleAdEligible: true },
    7: { treats: 500, packs: 0, doubleAdEligible: true },
    8: { treats: 500, packs: 0, doubleAdEligible: true },
    9: { treats: 0, packs: 1, boostBundle: 1, doubleAdEligible: false },
    10: { treats: 1000, packs: 0, doubleAdEligible: true },
    11: { treats: 1000, packs: 0, doubleAdEligible: true },
    12: {
        treats: 0,
        packs: 2,
        doubleAdEligible: false,
        cosmeticRewardIds: ["frame_journey", "title_overworld_savior"],
    },
    13: { treats: 0, packs: 1, boostBundle: 1, doubleAdEligible: false },
    14: { treats: 0, packs: 1, doubleAdEligible: false },
    15: { treats: 0, packs: 1, doubleAdEligible: false },
    16: { treats: 0, packs: 2, doubleAdEligible: false },
    17: { treats: 0, packs: 2, doubleAdEligible: false },
    18: { treats: 0, packs: 2, doubleAdEligible: false },
    19: {
        treats: 0,
        packs: 3,
        boostBundle: 1,
        doubleAdEligible: false,
        cosmeticRewardIds: ["frame_underworld_journey", "title_underworld_ruler"],
    },
};

function normalizeOverride(entry = {}) {
    return {
        treats: Math.max(
            0,
            Math.floor(
                Number(entry.treats ?? DEFAULT_STORY_LEVEL_TREATS) || 0
            )
        ),
        packs: Math.max(0, Math.floor(Number(entry.packs) || 0)),
        boostBundle: Math.max(0, Math.floor(Number(entry.boostBundle) || 0)),
        doubleAdEligible: entry.doubleAdEligible !== false,
        cosmeticRewardIds: Array.isArray(entry.cosmeticRewardIds)
            ? entry.cosmeticRewardIds.map(String)
            : [],
    };
}

export function getStoryLevelRewardOverride(levelId) {
    const id = Number(levelId);
    if (!Number.isFinite(id)) {
        return normalizeOverride({});
    }
    return normalizeOverride(STORY_LEVEL_REWARD_OVERRIDES[id] || {});
}

export function formatFeatureUnlockLabels(level = null) {
    if (!level) {
        return [];
    }

    const labels = [];

    if (Number(level.id) === STORE_UNLOCK_STORY_LEVEL) {
        labels.push("Store");
    }

    if (Number(level.id) === 12) {
        labels.push("Underworld access");
    }

    (level.unlocks || []).forEach((key) => {
        const label = FEATURE_UNLOCK_LABELS[key];
        if (label && !labels.includes(label)) {
            labels.push(label);
        }
    });

    return labels;
}

export function formatCosmeticRewardLabels(cosmeticRewardIds = []) {
    return (Array.isArray(cosmeticRewardIds) ? cosmeticRewardIds : [])
        .map((id) => getCosmeticById(id))
        .filter(Boolean)
        .map((item) => item.name);
}

export function formatPackRewardLabel(packCount = 0) {
    const count = Math.max(0, Math.floor(Number(packCount) || 0));
    if (count <= 0) {
        return "";
    }
    return count === 1 ? "1 Corgi Pack" : `${count} Corgi Packs`;
}

export function formatBoostBundleRewardLabel(bundleCount = 0) {
    const count = Math.max(0, Math.floor(Number(bundleCount) || 0));
    if (count <= 0) {
        return "";
    }
    return count === 1
        ? "Boost Bundle"
        : `${count} Boost Bundles`;
}

export function getStoryLevelRewardRow(levelId) {
    const level = getStoryLevelById(levelId);
    const override = getStoryLevelRewardOverride(levelId);

    return {
        levelId: Number(levelId),
        title: level?.title || `Level ${levelId}`,
        worldId: level?.worldId ?? null,
        treats: override.treats,
        packs: override.packs,
        packLabel: formatPackRewardLabel(override.packs),
        boostBundle: override.boostBundle,
        boostBundleLabel: formatBoostBundleRewardLabel(override.boostBundle),
        doubleAdEligible: override.doubleAdEligible && override.treats > 0,
        cosmeticRewardIds: override.cosmeticRewardIds,
        cosmeticLabels: formatCosmeticRewardLabels(override.cosmeticRewardIds),
        featureUnlocks: formatFeatureUnlockLabels(level),
    };
}

export function listStoryLevelRewardRows() {
    return STORY_LEVELS.map((level) => getStoryLevelRewardRow(level.id));
}

export function getStoryTreatsForLevelClear(levelId, didWin = false) {
    if (!didWin) {
        return 0;
    }
    return getStoryLevelRewardOverride(levelId).treats;
}

export function getStoryLevelPackReward(levelId, didWin = false) {
    if (!didWin) {
        return 0;
    }
    return getStoryLevelRewardOverride(levelId).packs;
}

export function getStoryLevelBoostBundleReward(levelId, didWin = false) {
    if (!didWin) {
        return 0;
    }
    return getStoryLevelRewardOverride(levelId).boostBundle;
}

export function getStoryLevelCosmeticRewardIds(levelId) {
    return getStoryLevelRewardOverride(levelId).cosmeticRewardIds;
}

export function isStoryLevelDoubleAdEligible(levelId) {
    const override = getStoryLevelRewardOverride(levelId);
    return override.doubleAdEligible && override.treats > 0;
}

function escapeMarkdownCell(value = "") {
    return String(value).replace(/\|/g, "\\|").replace(/\n/g, " ");
}

export function formatStoryLevelRewardsMarkdown() {
    const rows = listStoryLevelRewardRows();
    const lines = [
        "# Story Level Rewards",
        "",
        "Edit **`src/data/storyLevelRewards.js`** (`STORY_LEVEL_REWARD_OVERRIDES`) to change Treats, Corgi Packs, Boost Bundles, cosmetics, or 2× ad eligibility.",
        "Feature unlocks come from **`src/data/storyLevels.js`** (`unlocks` arrays).",
        "",
        "A **Boost Bundle** grants 1 Reveal + 1 Shield + 1 Freeze to Inventory.",
        "",
        "Regenerate this file after edits:",
        "",
        "```bash",
        "npm run story-rewards-doc",
        "```",
        "",
        "| Lv | Title | Treats | Packs | Boosts | 2× Ad | Cosmetics | Feature unlocks |",
        "|----|-------|--------|-------|--------|-------|-----------|-----------------|",
    ];

    rows.forEach((row) => {
        const packs = row.packLabel || "—";
        const boosts = row.boostBundleLabel || "—";
        const cosmetics =
            row.cosmeticLabels.length > 0
                ? row.cosmeticLabels.join("; ")
                : "—";
        const unlocks =
            row.featureUnlocks.length > 0
                ? row.featureUnlocks.join("; ")
                : "—";

        lines.push(
            `| ${row.levelId} | ${escapeMarkdownCell(row.title)} | ${row.treats.toLocaleString("en-US")} | ${escapeMarkdownCell(packs)} | ${escapeMarkdownCell(boosts)} | ${row.doubleAdEligible ? "Yes" : "No"} | ${escapeMarkdownCell(cosmetics)} | ${escapeMarkdownCell(unlocks)} |`
        );
    });

    const totalTreats = rows.reduce((sum, row) => sum + row.treats, 0);
    const totalPacks = rows.reduce((sum, row) => sum + row.packs, 0);
    const totalBoostBundles = rows.reduce(
        (sum, row) => sum + row.boostBundle,
        0
    );
    lines.push("");
    lines.push(
        `**Total Treats (all 19 clears once):** ${totalTreats.toLocaleString("en-US")} base · up to ${(totalTreats * 2).toLocaleString("en-US")} with every 2× ad`
    );
    lines.push(
        `**Total Corgi Packs (all 19 clears once):** ${totalPacks}`
    );
    lines.push(
        `**Total Boost Bundles (all 19 clears once):** ${totalBoostBundles}`
    );
    lines.push("");
    lines.push(
        "Loss attempts grant **0 Treats**, **0 Packs**, and **0 Boost Bundles**. Collection cards come from Corgi Packs / Endless board shinies, not flat per Story level."
    );
    lines.push("");

    return `${lines.join("\n")}`;
}
